import React, { useMemo, useState } from 'react'

import {
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Menu,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import { Add as AddIcon, Search } from '@material-ui/icons'
import { UseMutationResult } from '@tanstack/react-query'

import {
    Campaign,
    EnrichedValue,
    Prospect,
} from '../../../../models/Outreach/types'
import { ApartmentProspectsStatusSort } from '../../../../hooks/Outreach/useApartmentProspects'
import { prospectHasRevealedContact } from '../utils/prospectContact'

const useStyles = makeStyles((theme: Theme) => ({
    toolbar: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        alignItems: 'center',
        marginBottom: theme.spacing(2),
    },
    searchField: {
        minWidth: 200,
        flex: '1 1 180px',
        maxWidth: 280,
    },
    gradYearField: {
        minWidth: 140,
        maxWidth: 200,
    },
    emptyState: {
        padding: theme.spacing(4),
        textAlign: 'center',
        color: theme.palette.text.secondary,
    },
    statusChip: {
        height: 22,
        fontSize: '0.7rem',
        fontWeight: 600,
    },
    enrichmentChip: {
        height: 22,
        fontSize: '0.65rem',
    },
    clickableRow: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
    },
}))

function graduationYearOptions(): number[] {
    const currentYear = new Date().getFullYear()
    const years: number[] = []
    for (let y = currentYear - 1; y <= currentYear + 6; y += 1) {
        years.push(y)
    }
    return years
}

const EnrichmentCell: React.FC<{
    enriched?: EnrichedValue
    isLoading: boolean
    onEnrich: () => void
    allowManualEnrich?: boolean
}> = ({ enriched, isLoading, onEnrich, allowManualEnrich = true }) => {
    const classes = useStyles()
    const value = enriched?.value
    const lastAttempted = enriched?.last_attempted

    if (isLoading) return <CircularProgress size={18} />
    if (value) {
        return (
            <Typography variant="body2" noWrap style={{ maxWidth: 160 }}>
                {value}
            </Typography>
        )
    }
    if (!allowManualEnrich) {
        if (lastAttempted) {
            return (
                <Typography variant="body2" color="textSecondary">
                    Not Found
                </Typography>
            )
        }
        return (
            <Chip
                size="small"
                label="Needs Enrichment"
                className={classes.enrichmentChip}
            />
        )
    }
    return (
        <Button
            size="small"
            color="primary"
            onClick={(e) => {
                e.stopPropagation()
                onEnrich()
            }}
        >
            Enrich
        </Button>
    )
}

export interface SchoolProspectsTableProps {
    prospects: Prospect[]
    totalCount: number
    page: number
    pageSize: number
    searchInput: string
    onSearchInputChange: (value: string) => void
    onSearchSubmit: () => void
    onPageChange: (page: number) => void
    onPageSizeChange: (pageSize: number) => void
    isLoading: boolean
    graduationYears?: number[]
    onGraduationYearsChange?: (years: number[]) => void
    hasEmail?: boolean
    onHasEmailChange?: (value: boolean) => void
    hasPhone?: boolean
    onHasPhoneChange?: (value: boolean) => void
    statusSort?: ApartmentProspectsStatusSort
    onStatusSortChange?: (value: ApartmentProspectsStatusSort) => void
    enrichingProspects?: Array<{ id: number; field: string }>
    enrichProspectField?: UseMutationResult<
        unknown,
        unknown,
        { prospectId: number; field: 'email' | 'phone' | 'linkedinProfile' },
        unknown
    >
    campaigns?: Campaign[]
    addProspectToCampaign?: UseMutationResult<
        unknown,
        unknown,
        { prospectId: number; campaignId: number },
        unknown
    >
    showCampaignColumn?: boolean
    showStatusColumn?: boolean
    /** When false, enrichment columns show value / Not Found / Needs Enrichment only. */
    allowManualEnrich?: boolean
    emptyTitle?: string
    emptyDescription?: string
    onRowClick?: (prospect: Prospect) => void
}

export const SchoolProspectsTable: React.FC<SchoolProspectsTableProps> = ({
    prospects,
    totalCount,
    page,
    pageSize,
    searchInput,
    onSearchInputChange,
    onSearchSubmit,
    onPageChange,
    onPageSizeChange,
    isLoading,
    graduationYears = [],
    onGraduationYearsChange,
    hasEmail = false,
    onHasEmailChange,
    hasPhone = false,
    onHasPhoneChange,
    statusSort = 'prospect_first',
    onStatusSortChange,
    enrichingProspects = [],
    enrichProspectField,
    campaigns = [],
    addProspectToCampaign,
    showCampaignColumn = true,
    showStatusColumn = false,
    allowManualEnrich = true,
    emptyTitle = 'No prospects yet',
    emptyDescription = 'Students for this school will appear here as the pool is populated.',
    onRowClick,
}) => {
    const classes = useStyles()
    const [campaignMenu, setCampaignMenu] = useState<{
        anchor: HTMLElement
        prospectId: number
    } | null>(null)

    const yearOptions = useMemo(() => graduationYearOptions(), [])
    const showFilters =
        Boolean(onGraduationYearsChange) ||
        Boolean(onHasEmailChange) ||
        Boolean(onHasPhoneChange)

    const isEnriching = (prospectId: number, field: string) =>
        enrichingProspects.some((e) => e.id === prospectId && e.field === field)

    const customFieldKeys = useMemo(() => {
        const keys = new Set<string>()
        prospects.forEach((prospect) => {
            if (prospect.custom_data) {
                Object.keys(prospect.custom_data).forEach((key) =>
                    keys.add(key),
                )
            }
        })
        return Array.from(keys).sort()
    }, [prospects])

    const handleCampaignSelect = (campaign: Campaign) => {
        if (!campaignMenu || !addProspectToCampaign) return
        addProspectToCampaign.mutate({
            prospectId: campaignMenu.prospectId,
            campaignId: campaign.id,
        })
        setCampaignMenu(null)
    }

    const handleStatusSortClick = () => {
        if (!onStatusSortChange) return
        onStatusSortChange(
            statusSort === 'prospect_first' ? 'target_first' : 'prospect_first',
        )
    }

    return (
        <>
            <Box className={classes.toolbar}>
                <TextField
                    className={classes.searchField}
                    size="small"
                    variant="outlined"
                    placeholder="Search by name..."
                    value={searchInput}
                    onChange={(e) => onSearchInputChange(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') onSearchSubmit()
                    }}
                    InputProps={{
                        endAdornment: (
                            <IconButton size="small" onClick={onSearchSubmit}>
                                <Search />
                            </IconButton>
                        ),
                    }}
                />
                {showFilters && onGraduationYearsChange && (
                    <TextField
                        className={classes.gradYearField}
                        select
                        size="small"
                        variant="outlined"
                        value={graduationYears}
                        onChange={(e) => {
                            const raw = e.target.value
                            const selected = Array.isArray(raw)
                                ? raw
                                : String(raw).split(',').filter(Boolean)
                            onGraduationYearsChange(
                                selected.map((y) => Number(y)),
                            )
                        }}
                        SelectProps={{
                            multiple: true,
                            displayEmpty: true,
                            renderValue: (selected) => {
                                const years = selected as number[]
                                if (!years.length) return 'All Grad Years'
                                return years.join(', ')
                            },
                        }}
                    >
                        {yearOptions.map((year) => (
                            <MenuItem key={year} value={year}>
                                {year}
                            </MenuItem>
                        ))}
                    </TextField>
                )}
                {showFilters && onHasEmailChange && (
                    <Chip
                        size="small"
                        label="Has email"
                        clickable
                        color={hasEmail ? 'primary' : 'default'}
                        onClick={() => onHasEmailChange(!hasEmail)}
                    />
                )}
                {showFilters && onHasPhoneChange && (
                    <Chip
                        size="small"
                        label="Has phone"
                        clickable
                        color={hasPhone ? 'primary' : 'default'}
                        onClick={() => onHasPhoneChange(!hasPhone)}
                    />
                )}
            </Box>

            {isLoading && <CircularProgress size={28} />}

            {!isLoading && prospects.length === 0 && (
                <Box className={classes.emptyState}>
                    <Typography variant="h6">{emptyTitle}</Typography>
                    <Typography variant="body2">{emptyDescription}</Typography>
                </Box>
            )}

            {!isLoading && prospects.length > 0 && (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                {showStatusColumn && (
                                    <TableCell
                                        sortDirection={
                                            statusSort === 'prospect_first'
                                                ? 'desc'
                                                : 'asc'
                                        }
                                    >
                                        {onStatusSortChange ? (
                                            <TableSortLabel
                                                active
                                                direction={
                                                    statusSort ===
                                                    'prospect_first'
                                                        ? 'desc'
                                                        : 'asc'
                                                }
                                                onClick={handleStatusSortClick}
                                            >
                                                Status
                                            </TableSortLabel>
                                        ) : (
                                            'Status'
                                        )}
                                    </TableCell>
                                )}
                                <TableCell>Grad year</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Phone</TableCell>
                                <TableCell>LinkedIn</TableCell>
                                {customFieldKeys.map((fieldKey) => (
                                    <TableCell key={fieldKey}>
                                        {fieldKey}
                                    </TableCell>
                                ))}
                                {showCampaignColumn && (
                                    <TableCell>Campaign</TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {prospects.map((prospect) => (
                                <TableRow
                                    key={prospect.id}
                                    hover={Boolean(onRowClick)}
                                    className={
                                        onRowClick
                                            ? classes.clickableRow
                                            : undefined
                                    }
                                    onClick={
                                        onRowClick
                                            ? () => onRowClick(prospect)
                                            : undefined
                                    }
                                >
                                    <TableCell>{prospect.name}</TableCell>
                                    {showStatusColumn && (
                                        <TableCell>
                                            {prospectHasRevealedContact(
                                                prospect,
                                            ) ? (
                                                <Chip
                                                    size="small"
                                                    label="Prospect"
                                                    color="primary"
                                                    className={
                                                        classes.statusChip
                                                    }
                                                />
                                            ) : (
                                                <Chip
                                                    size="small"
                                                    label="Target"
                                                    className={
                                                        classes.statusChip
                                                    }
                                                />
                                            )}
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        {prospect.graduationYear ?? '—'}
                                    </TableCell>
                                    <TableCell>
                                        <EnrichmentCell
                                            enriched={prospect.email}
                                            isLoading={isEnriching(
                                                prospect.id,
                                                'email',
                                            )}
                                            allowManualEnrich={
                                                allowManualEnrich
                                            }
                                            onEnrich={() =>
                                                enrichProspectField?.mutate({
                                                    prospectId: prospect.id,
                                                    field: 'email',
                                                })
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <EnrichmentCell
                                            enriched={prospect.phone}
                                            isLoading={isEnriching(
                                                prospect.id,
                                                'phone',
                                            )}
                                            allowManualEnrich={
                                                allowManualEnrich
                                            }
                                            onEnrich={() =>
                                                enrichProspectField?.mutate({
                                                    prospectId: prospect.id,
                                                    field: 'phone',
                                                })
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <EnrichmentCell
                                            enriched={prospect.linkedinProfile}
                                            isLoading={isEnriching(
                                                prospect.id,
                                                'linkedinProfile',
                                            )}
                                            allowManualEnrich={
                                                allowManualEnrich
                                            }
                                            onEnrich={() =>
                                                enrichProspectField?.mutate({
                                                    prospectId: prospect.id,
                                                    field: 'linkedinProfile',
                                                })
                                            }
                                        />
                                    </TableCell>
                                    {customFieldKeys.map((fieldKey) => (
                                        <TableCell key={fieldKey}>
                                            {prospect.custom_data?.[fieldKey]
                                                ? String(
                                                      prospect.custom_data[
                                                          fieldKey
                                                      ],
                                                  )
                                                : '—'}
                                        </TableCell>
                                    ))}
                                    {showCampaignColumn && (
                                        <TableCell>
                                            {prospect.active_campaign ? (
                                                <Typography variant="body2">
                                                    {
                                                        prospect.active_campaign
                                                            .name
                                                    }
                                                </Typography>
                                            ) : (
                                                <Button
                                                    size="small"
                                                    startIcon={<AddIcon />}
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        setCampaignMenu({
                                                            anchor:
                                                                e.currentTarget,
                                                            prospectId:
                                                                prospect.id,
                                                        })
                                                    }}
                                                >
                                                    Add
                                                </Button>
                                            )}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={totalCount}
                        page={page - 1}
                        onChangePage={(
                            _event: React.MouseEvent<HTMLButtonElement> | null,
                            newPage: number,
                        ) => onPageChange(newPage + 1)}
                        rowsPerPage={pageSize}
                        onChangeRowsPerPage={(
                            event: React.ChangeEvent<HTMLInputElement>,
                        ) => onPageSizeChange(parseInt(event.target.value, 10))}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </TableContainer>
            )}

            {showCampaignColumn && addProspectToCampaign && (
                <Menu
                    anchorEl={campaignMenu?.anchor}
                    open={Boolean(campaignMenu)}
                    onClose={() => setCampaignMenu(null)}
                >
                    {campaigns.map((campaign) => (
                        <MenuItem
                            key={campaign.id}
                            onClick={() => handleCampaignSelect(campaign)}
                        >
                            {campaign.name}
                        </MenuItem>
                    ))}
                </Menu>
            )}
        </>
    )
}
